import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {MeetingRepositoryForResident} from './MeetingRepositoryForResident'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {Nullable} from '../../app/TypeHelpers'
import MeetingSimple from './model/MeetingSimple'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {Box} from '@mui/material'
import {IconBox, MeetingIcon, MeetingIconBack, MiddleFontSpan} from './AssocDirectorCommon'
import {CardWidth, EllipsisText, StyledCard} from '../reservations/components/CommonStyle'
import {ContentBody, MeetingTitle} from './AssocDirectorResidentCommon'
import {DisplayItemDividerForPageTop, DisplayItemTitle} from '../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {currentMedia} from '../../styledComponents/ResponsiveConvenience'
import {LeftRowFlexContainer} from '../../styledComponents/FlexComponents'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faClock} from '@fortawesome/free-regular-svg-icons'
import {PlainBackLink} from '../../styledComponents/Links'
import moment, {Moment} from 'moment'
import {
    NatGetCurrentDateTime,
} from '../../__NoAutomatedTests/components/currentDateTime/repository/NatGetCurrentDateTime'
import {Colors} from '../../styledComponents/Constants'
import {MeetingHelper} from './utils/MeetingHelper'

interface Props {
    condominiumCd: string
    meetingRepoForResident: MeetingRepositoryForResident
    currentDateTimeRepo: NatCurrentDateTimeRepo
}

interface State {
    meetings: MeetingSimple[]
    currentDateTime: Nullable<Moment>
}

class ResidentMeetingListPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)

        this.state = {
            meetings: [],
            currentDateTime: null,
        }
    }

    async componentDidMount(): Promise<void> {
        const [
            meetings,
            currentDateTime,
        ] = await Promise.all([
            this.getMeetings(),
            this.getCurrentDateTime(),
        ])
        if (meetings === null) {
            return
        }
        if (currentDateTime === null || currentDateTime.details === null) {
            return
        }

        this.setState({
            meetings: meetings,
            currentDateTime: moment(currentDateTime.details.currentDateTime),
        })
    }

    async getMeetings(): Promise<Nullable<MeetingSimple[]>> {
        const {meetingRepoForResident, condominiumCd, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let meetings: Nullable<MeetingSimple[]>
        try {
            meetings = await NatAsyncCallFunction(
                meetingRepoForResident.getMeetings.bind(
                    meetingRepoForResident,
                    condominiumCd,
                ), navigate)

            return meetings

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let currentDateTime: Nullable<NatGetCurrentDateTime> = null
        try {
            currentDateTime = await NatAsyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                navigate,
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return currentDateTime
    }

    handleClickDisplayMeetingDetailButton = (selectedMeetingId: number) => (): void => {
        const {condominiumCd, navigate} = this.props
        navigate(
            `/resident/condominiums/${condominiumCd}/meetings/${selectedMeetingId}`,
            {state: {previousPageUrl: `/resident/condominiums/${condominiumCd}/meetings`}},
        )
    }

    renderMeetingCardContents(meeting: MeetingSimple): ReactNode {
        return (
            <>
                <MeetingCardTitleArea>
                    <LeftRowFlexContainer>
                        <MeetingIconBack><MeetingIcon/></MeetingIconBack>
                        <Box minWidth={'10px'}/>
                        <MeetingTitle>
                            <EllipsisText
                                lineHeightInPixel={24}
                                numOfLines={2}
                            >
                                {meeting.name}
                            </EllipsisText>
                        </MeetingTitle>
                    </LeftRowFlexContainer>
                </MeetingCardTitleArea>
                <MeetingCardDivider/>
                <Box margin={'0px 10px 4px 10px'}>
                    <LeftRowFlexContainer>
                        <IconBox><FontAwesomeIcon icon={faClock}/></IconBox>
                        <NoWrapText>{MeetingHelper.getMeetingDateString(meeting)}</NoWrapText>
                    </LeftRowFlexContainer>
                </Box>
            </>
        )
    }

    render(): ReactNode {
        if (!this.state.currentDateTime) {
            return null
        }

        const {condominiumCd} = this.props
        const {meetings, currentDateTime} = this.state
        const nextMeeting = MeetingHelper.getNextMeeting(meetings, currentDateTime)

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/assoc-top`}
            >
                戻る
            </PlainBackLink>
            <Box height={'14px'}/>
            <DisplayItemDividerForPageTop/>
            <Box height={'15px'}/>
            <DisplayItemTitle>会議一覧</DisplayItemTitle>
            <ContentBody>
                {
                    meetings.length === 0 ?
                        <>
                            <Box margin={'10px'}>
                                <MiddleFontSpan>会議はありません</MiddleFontSpan>
                            </Box>
                            <Box height={'15px'}/>
                        </>
                        :
                        <MeetingCardList>
                            {meetings.map((meeting, index) => {
                                return <MeetingCard
                                    id={`MeetingId-${meeting.id}`}
                                    onClick={this.handleClickDisplayMeetingDetailButton(meeting.id)}
                                    key={index}>
                                    {this.renderMeetingCardContents(meeting)}
                                    {
                                        nextMeeting?.id === meeting.id &&
                                        <NextRibbon>
                                            <NextRibbonText>次の会議</NextRibbonText>
                                        </NextRibbon>
                                    }
                                </MeetingCard>
                            })}
                        </MeetingCardList>
                }
            </ContentBody>
        </>
    }
}

export default createResidentPage(ResidentMeetingListPage)

const MeetingCardList = styled.div`
  display: flex;
  box-sizing: border-box;
  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    align-items: center;
  `}
  ${currentMedia.greaterThan('pc')`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  `}

  width: 100%;
`

const MeetingCard = styled(StyledCard)`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: left;
  align-items: flex-start;
  box-shadow: 0px 1px 8px rgba(112, 112, 112, 0.4);
  margin-top: 20px;
  ${CardWidth}
`

const MeetingCardDivider = styled.div`
  width: 100%;
  height: 1px;
  border: 1px solid #F1F1F1;
  box-sizing: border-box;
`

const NoWrapText = styled.span`
  white-space: nowrap;
`

const MeetingCardTitleArea = styled.div`
  height: 57px;
  margin: 0px 10px 3px 10px;

  display: flex;
  flex-direction: column;
  justify-content: center;
`

const NextRibbon = styled.div`
  position: absolute;
  top: 10px;
  right: -26px;
  width: 100px;
  height: 24px;
  transform: rotate(40deg);
  font-size: 12px;
  color: ${Colors.Surface};
  background-color: ${Colors.Primary};
`

const NextRibbonText = styled.div`
  position: absolute;
  top: 3px;
  left: 25px;
`
